import {WidgetRendererProps} from '@app/dashboard/biolink/biolink-editor/content/widgets/types/widget-renderer-props';
import clsx from 'clsx';
import {RemoteFavicon} from '@common/ui/remote-favicon';
import {
  formatEmbedURL,
  formatURI,
} from '@app/dashboard/biolink/biolink-editor/content/widgets/spotify-widget/spotify-uri';
import {SpotifyWidget} from '@app/dashboard/biolink/biolink-editor/content/widgets/spotify-widget/spotify-widget-dialog';
import {useEffect, useRef} from 'react';
import {apiClient} from '@common/http/query-client';

declare global {
  interface Window {
    onSpotifyIframeApiReady?: (IFrameAPI: any) => void;
    SpotifyIFrameAPI?: any;
  }
}

export function SpotifyWidgetRenderer({
  widget,
  variant,
}: WidgetRendererProps<SpotifyWidget>) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (variant === 'editor') return;

    // Setup global callback if not exists
    if (!window.onSpotifyIframeApiReady) {
      window.onSpotifyIframeApiReady = (IFrameAPI: any) => {
        window.SpotifyIFrameAPI = IFrameAPI;
        window.dispatchEvent(new CustomEvent('spotify-api-ready'));
      };
    }

    const scriptId = 'spotify-iframe-api';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://open.spotify.com/embed/iframe-api/v1';
      script.async = true;
      document.body.appendChild(script);
    }

    const run = (IFrameAPI: any) => {
      if (!iframeRef.current) return;
      const element = iframeRef.current;
      const options = {
        uri: formatURI(widget.config.url),
      };

      IFrameAPI.createController(element, options, (EmbedController: any) => {
        EmbedController.addListener('playback_started', () => {
          apiClient
            .post('biolink/widget/playback', {
              widget_id: widget.id,
            })
            .catch(() => {});
        });
      });
    };

    if (window.SpotifyIFrameAPI) {
      run(window.SpotifyIFrameAPI);
    } else {
      const handler = () => {
        if (window.SpotifyIFrameAPI) run(window.SpotifyIFrameAPI);
      };
      window.addEventListener('spotify-api-ready', handler);
      return () => window.removeEventListener('spotify-api-ready', handler);
    }
  }, [variant, widget.config.url, widget.id]);

  if (!widget.config.url) return null;

  if (variant === 'editor') {
    return (
      <div className="flex items-center gap-8">
        <RemoteFavicon url={widget.config.url} />
        <a
          href={widget.config.url}
          target="_blank"
          className="text-muted text-sm hover:underline whitespace-nowrap overflow-hidden overflow-ellipsis max-w-[80%]"
          rel="noreferrer"
        >
          {widget.config.url}
        </a>
      </div>
    );
  }

  const embedURL = formatEmbedURL(widget.config.url);
  return (
    <iframe
      ref={iframeRef}
      className={clsx(
        'w-full rounded shadow-lg',
        getEmbedHeight(widget.config.type)
      )}
      loading="lazy"
      src={embedURL}
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
    />
  );
}

function getEmbedHeight(type?: string) {
  switch (type) {
    case 'track':
      return 'h-80';
    default:
      return 'h-[152px]';
  }
}
